import Vue from "vue";
import VueRouter from "vue-router";
import { reportForcedLogout } from "@/utils/forcedLogout";
import store from "../store";
import { createClient } from "../vue-apollo";
import IS_EXPIRED from "../graphql/query/isExpiredConnection.gql";
import cache from "./cache";
import routes from "./routes";
import { hasGuestToken, isGuestTokenExpired } from "./guestAccess";
import { usePostHog } from "@/composables/usePostHog";
import i18n from "@/i18n";
import { detectFromUser, saveLanguage } from "@/i18n/languageDetector";
import { loadFreshworksWidget } from "@/lib/freshworks";
import { isEmbedPath } from "@/utils/embedPath";

Vue.use(VueRouter);

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
  },
  routes,
});

// Initialize PostHog
const { posthog } = usePostHog();

router.beforeEach(async (to, from, next) => {
  // DL-452: the embed routes are public and have no legacy session, but they are served from the
  // SAME origin as the legacy app — so they share localStorage. Without this guard everything
  // below runs inside the iframe: reading the token from localStorage, dispatching users/getUser
  // (authenticated GraphQL on every navigation), running IS_EXPIRED and, on a stale token,
  // clearLanguage() + signout(). That hits any developer or QA with a legacy session open in the
  // browser they test the embed with.
  //
  // The authorisation checks further down are already opt-in through `meta.requires`; what needs
  // skipping is the session work above them.
  if (to.meta?.embed || isEmbedPath(to.path)) {
    next();
    return;
  }

  try {
    // Acceso de invitado (líder sin cuenta) a /conference con ?guestToken.
    // Salta el login wall y las validaciones de auth/group/role. La firma la
    // valida el backend; aquí solo se detecta un token vencido para mostrar
    // "enlace vencido" en vez de mandar a Login.
    if (hasGuestToken(to)) {
      if (isGuestTokenExpired(to.query.guestToken)) {
        store.dispatch("notifications/callSnackbar", {
          color: "error",
          content: i18n.t("conference.guest.linkExpired"),
          timeout: 8000,
        });
        return next(false);
      }
      return next();
    }

    const apolloClient = createClient();
    let isLoggedIn = store.getters["users/isLoggedIn"];
    let isVerified = store.getters["users/isVerified"];
    let userGroup = store.getters["users/userGroup"];
    let userRoles = store.getters["users/userRoles"];
    let activeRole = store.getters["users/activeRole"];

    if (isLoggedIn) {
      const {
        data: {
          User: { expireConnection },
        },
      } = await apolloClient.query({
        query: IS_EXPIRED,
        fetchPolicy: "no-cache",
      });

      if (expireConnection) {
        reportForcedLogout({
          trigger: "router_guard_expireConnection",
          page: to.name,
          userId: store.getters["users/user"]?._id,
          tags: { "session.from_page": from.name || "unknown" },
        });
        store.commit("users/SET_USER_DATA", { user: null });
        store.commit("users/SET_TOKEN_DATA", { token: null });
        store.dispatch("users/signout");
      }
      isLoggedIn = store.getters["users/isLoggedIn"];
      isVerified = store.getters["users/isVerified"];
      userGroup = store.getters["users/userGroup"];
      userRoles = store.getters["users/userRoles"];
      activeRole = store.getters["users/activeRole"];
    }
    // Si existen usuario y token en Local Storage (y no loggeado), setear en Store
    let tokenString = to.name === "Signout" ? null : localStorage.getItem("token");

    if (tokenString && !isLoggedIn) {
      try {
        store.commit("users/SET_TOKEN_DATA", { token: JSON.parse(tokenString) });
        await store.dispatch("users/getUser", { apolloClient });
        isLoggedIn = store.getters["users/isLoggedIn"];
        isVerified = store.getters["users/isVerified"];
        userGroup = store.getters["users/userGroup"];
        userRoles = store.getters["users/userRoles"];
        activeRole = store.getters["users/activeRole"];

        // Sincronizar idioma del usuario con i18n al restaurar sesión
        const user = store.getters["users/user"];
        const userLang = detectFromUser(user);
        if (userLang) {
          i18n.locale = userLang;
          saveLanguage(userLang);
        }
      } catch (restoreError) {
        // Token is invalid/expired — clear it and continue as logged-out
        store.commit("users/SET_TOKEN_DATA", { token: null });
        store.commit("users/SET_USER_DATA", { user: null });
        localStorage.removeItem("token");
      }
    }

    // Rutas protegidas
    if (
      isLoggedIn &&
      to.matched.some((record) => record.meta?.requires?.loggedOut)
    ) {
      console.warn("[Ruta protegida] Usuario no autorizado [Logged in]");
      next({ name: "Signout" });
      return;
    }
    // Para cambiar o preparar ambientes para roles especificos
    if (
      isLoggedIn &&
      to.matched.some((record) => record.meta?.requires?.checkRole)
    ) {
      console.warn("[Ruta protegida] Usuario no autorizado [checkRole]");
      console.warn("CHEQUEAR ROLE PARA META ASIGNAR LAYOUT");
      // Se asigna otro layout para un rol especifico
      if (
        (userGroup === "customer" &&
          activeRole === "admin" &&
          ["Wizard", "Quiz", "Sessions", "Chat", "Profile"].includes(to.name)) ||
        (userGroup === "customer" &&
          activeRole === "coachee" &&
          ["Wizard", "Quiz", "Sessions", "Chat", "Profile"].includes(to.name))
      ) {
        to.meta.layout = "v2/Main";
      }
    }
    if (!isLoggedIn && to.matched.some((record) => record.meta?.requires?.auth)) {
      console.warn("[Ruta protegida] Usuario no autorizado [Auth]");
      // Esta Ruta debe ir a Registro cuando se habilite el registro
      cache.set(to);
      next({ name: "Login" });
      return;
    }
    if (
      !isVerified &&
      isLoggedIn &&
      to.matched.some((record) => record.meta?.requires?.verified)
    ) {
      console.warn("[Ruta protegida] Usuario no autorizado [Verified]");
      next({ name: "Verify" });
      return;
    }
    if (
      isVerified &&
      to.matched.some((record) => record.meta?.requires?.unverified)
    ) {
      console.warn("[Ruta protegida] Usuario no autorizado [Unverified]");
      next({ name: "Home" });
      return;
    }
    if (
      to.matched.some((record) => {
        const requiresGroups = record.meta?.requires?.groups?.length > 0;
        const hasRequiredGroup = record.meta?.requires?.groups?.includes(userGroup);
        const groupRequirement = requiresGroups && !hasRequiredGroup;
        return groupRequirement;
      })
    ) {
      const youShould = to.meta.requires.groups.join(", ");
      console.warn(
        `[Ruta protegida] Usuario no autorizado - Groups [${youShould}] `
      );
      next({ name: "Home" });
      return;
    }
    if (
      to.matched.some((record) => {
        const requiresRoles = record.meta?.requires?.roles?.length > 0;
        if (record?.meta?.requires?.activeRole)
          return requiresRoles && !record.meta.requires.roles.includes(activeRole);
        const userHasSomeRequiredRole = userRoles?.some((role) =>
          record.meta?.requires?.roles?.includes(role)
        );
        const roleRequirement = requiresRoles && !userHasSomeRequiredRole;
        return roleRequirement;
      })
    ) {
      const youShould = to.meta.requires.roles.join(", ");
      console.warn(`[Ruta protegida] Usuario no autorizado - role [${youShould}] `);
      next({ name: "Home" });
      return;
    }
    if (
      to.matched.some((record) => {
        return (
          (activeRole === "coach" || activeRole === "coachee") &&
          record.meta?.requires?.wizardStep &&
          store.getters["users/getWelcomeStep"] < record.meta?.requires?.wizardStep
        );
      })
    ) {
      console.warn(
        `[Ruta protegida] Usuario no ha completado asistente. Path: ${to.fullPath}`
      );
      next({ name: "Wizard" });
      return;
    }

    next(); // Always call next()!
  } catch (error) {
    console.error(error);
    const requiresAuth = to.matched.some((r) => r.meta?.requires?.auth);
    requiresAuth ? next({ name: "Login" }) : next();
  }
});

router.afterEach((to, from) => {
  // DL-452: the embed has no legacy user and the parent (Horizon) is the one emitting analytics.
  // Cutting out here switches off two things at once: PostHog's $pageview inside the iframe, and
  // `cookies/activity`, which accumulates and on the 4th navigation fires the LogActivity mutation
  // — authenticated, so the errorLink would bounce the iframe to /acceder.
  if (to.meta?.embed || isEmbedPath(to.path)) return;

  const userGroup = store.getters["users/userGroup"];
  const userRoles = store.getters["users/userRoles"];
  if (userGroup) {
    loadFreshworksWidget(userGroup, userRoles);
  }

  store.dispatch("cookies/activity", {
    path: to.path,
    activity: "pageLoad",
    apolloClient: createClient(),
  });

  // Track page views with PostHog
  if (posthog) {
    try {
      posthog.capture("$pageview", {
        $current_url: to.fullPath,
        page_name: to.name,
        route_params: to.params,
        route_query: to.query,
        from_page: from?.name,
        user_group: store.getters["users/userGroup"],
        active_role: store.getters["users/activeRole"],
        is_logged_in: store.getters["users/isLoggedIn"],
      });

      console.log(`PostHog: Page view tracked for ${to.name} (${to.fullPath})`);
    } catch (error) {
      console.warn("PostHog tracking failed:", error);
    }
  }
});

export default router;

export const createRouter = () => {
  return new VueRouter({
    mode: "history",
    base: process.env.BASE_URL,
    routes,
  });
};
